import { useId, type ReactElement } from "react";
import { BODY_TYPE } from "odyssey-shared";
import { classes } from "./classes.ts";
import { useInteraction } from "./interaction.tsx";
import { KIT_METRICS, logical } from "./metrics.ts";
import { fieldFrame, frameClass } from "./state.ts";

/**
 * A text area: the kit's text field over several lines (`TextField.tsx`), for a description. It has the field's
 * pixel-font label, its counter beside the label when asked for, its frame and its aether focus frame, and sets
 * Noto Sans; its box is as tall as its lines and scrolls inside itself past them, and it is never resized by hand.
 * The input router counts a text area as a text field (CLIENT.md 7.3), so every key types while it has focus,
 * Enter starts a new line, and the game moves nothing.
 */

/**
 * The fewest lines a text area shows.
 */
const MIN_LINES = 2;

/**
 * A text area's box height for a number of lines: the field's box and a line for each line past the first.
 *
 * @param lines How many lines show, at least two.
 * @returns The height in logical pixels.
 * @example
 * textAreaHeight(5);
 * // the field's box height plus four lines of the field role
 */
export function textAreaHeight(lines: number): number {
  return KIT_METRICS.field.boxHeight + (Math.max(MIN_LINES, Math.floor(lines)) - 1) * BODY_TYPE.field.lineHeight;
}

/**
 * A text area.
 *
 * @param props The label; the `value` and `onChange`; `lines`, how many show; `maxLength`, which stops the typing;
 *   `counter`, which counts the characters against `maxLength` beside the label; `disabled`; and `name`.
 * @returns The field.
 * @example
 * <TextArea label="Description" value={description} onChange={setDescription} lines={5} maxLength={4000} />
 */
export function TextArea({ label, value, onChange, lines = 4, maxLength, counter = false, disabled = false, name }: { readonly label: string; readonly value: string; readonly onChange: (value: string) => void; readonly lines?: number; readonly maxLength?: number; readonly counter?: boolean; readonly disabled?: boolean; readonly name?: string }): ReactElement {
  const id = useId();
  const { state, handlers } = useInteraction({ disabled, focus: "any" });
  const frame = fieldFrame({ ...state, disabled, error: false });
  return (
    <div className={classes("ot-field", state.focus && "is-focus", disabled && "is-disabled")} data-kit="field">
      <div className="ot-field__head" data-kit="field-head" style={{ height: logical(KIT_METRICS.field.head) }}>
        <label className="ot-field__label" htmlFor={id}>
          {label}
        </label>
        {counter && maxLength !== undefined ? (
          <span className={classes("ot-field__count", value.length > maxLength && "is-over")} aria-hidden="true">
            {`${value.length}/${maxLength}`}
          </span>
        ) : null}
      </div>
      <div className={classes("ot-field__box", frameClass(frame))} data-kit="field-box" style={{ height: logical(textAreaHeight(lines)), marginTop: logical(KIT_METRICS.field.headGap) }} onPointerEnter={handlers.onPointerEnter} onPointerLeave={handlers.onPointerLeave} onPointerCancel={handlers.onPointerCancel}>
        <textarea id={id} className="ot-field__input ot-field__area ot-scroll" name={name} value={value} maxLength={maxLength} disabled={disabled} spellCheck={false} onChange={(event) => onChange(event.currentTarget.value)} onFocus={handlers.onFocus} onBlur={handlers.onBlur} style={{ padding: `${logical(KIT_METRICS.field.paddingY)} ${logical(KIT_METRICS.field.paddingX)}` }} />
      </div>
    </div>
  );
}
